"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  year: "2018",
  tag: "ESTABLISHED",
  title: "Revenue precision begins.",
  description: "HotelRevBaBa was launched to bring data-driven revenue management to independent hotels, focusing on OTA optimization and market share growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consulting_start.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consulting_start.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consulting_start.png-wvc-sizes"
}, {
  year: "2020",
  tag: "TECH INTEGRATION",
  title: "Advanced tech stack audits.",
  description: "We expanded our services to include full hospitality tech stack recommendations, helping properties integrate PMS and RMS for better data flow.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_audit.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_audit.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_audit.webp-wvc-sizes"
}, {
  year: "2022",
  tag: "GLOBAL REACH",
  title: "Scaling revenue strategies.",
  description: "Sunil Singh led our expansion into international markets, solving complex demand challenges for boutique hotels and large-scale property investors.",
  image: "",
  srcset: "",
  sizes: ""
}, {
  year: "2023",
  tag: "REVENUE AUDITS",
  title: "Deep-dive performance analysis.",
  description: "We launched our signature Revenue Audit service, providing hotel owners with actionable insights to maximize RevPAR and net profit margins.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.png-wvc-sizes"
}, {
  year: "2024",
  tag: "STRATEGIC PARTNERS",
  title: "Data-driven growth models.",
  description: "We refined our proprietary revenue modeling, helping our partners achieve consistent year-over-year growth through precise OTA strategy.",
  image: "",
  srcset: "",
  sizes: ""
}, {
  year: "2025",
  tag: "INDUSTRY LEADERS",
  title: "A decade of revenue mastery.",
  description: "Today, we manage revenue for a diverse portfolio, ensuring every client benefits from our deep expertise in hospitality technology and market trends.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/industry_leadership.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/industry_leadership.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/industry_leadership.webp-wvc-sizes"
}];
function MilestoneRow({
  milestone,
  index
}: {
  milestone: (typeof milestones)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 mb-20 md:mb-28 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="hidden md:block absolute left-1/2 top-8 -translate-x-1/2 z-10">
        <div className="relative flex items-center justify-center">
          <div className="size-4 rounded-lg" style={{
          backgroundColor: "var(--chart-2)"
        }} />
          <div className="absolute size-8 rounded-lg opacity-30" style={{
          backgroundColor: "var(--chart-2)"
        }} />
        </div>
      </div>

      <div className={`${isLeft ? "md:pr-16 md:text-right order-1" : "md:pr-16 md:text-right order-1 md:order-1 md:invisible"}`}>
        {isLeft && <div className="relative">
            <div className="font-serif text-6xl md:text-7xl lg:text-[5rem] leading-none font-light tracking-tight mb-4" style={{
          color: "var(--primary)",
          fontVariantNumeric: "tabular-nums"
        }}>
              {milestone.year}
            </div>
            <div className="hidden md:block absolute top-10 right-0 h-px" style={{
          backgroundColor: "var(--chart-2)",
          width: "calc(4rem - 0.5rem)",
          transform: "translateX(100%)"
        }} />
            <MilestoneCard milestone={milestone} align="right" />
          </div>}
      </div>

      <div className={`${!isLeft ? "md:pl-16 order-2" : "md:pl-16 order-2 md:invisible"}`}>
        {!isLeft && <div className="relative">
            <div className="font-serif text-6xl md:text-7xl lg:text-[5rem] leading-none font-light tracking-tight mb-4" style={{
          color: "var(--primary)",
          fontVariantNumeric: "tabular-nums"
        }}>
              {milestone.year}
            </div>
            <div className="hidden md:block absolute top-10 left-0 h-px" style={{
          backgroundColor: "var(--chart-2)",
          width: "calc(4rem - 0.5rem)",
          transform: "translateX(-100%)"
        }} />
            <MilestoneCard milestone={milestone} align="left" />
          </div>}
      </div>
    </div>;
}
function MilestoneCard({
  milestone,
  align
}: {
  milestone: (typeof milestones)[number];
  align: "left" | "right";
}) {
  return <div className={`bg-card border border-border rounded-xl p-6 shadow-sm ${align === "right" ? "md:text-right" : "md:text-left"} text-left`}>
      <div className="font-mono text-xs tracking-[0.2em] uppercase mb-3" style={{
      color: "var(--chart-2)"
    }}>
        {milestone.tag}
      </div>
      <h3 className="font-serif font-default text-xl md:text-2xl leading-snug mb-3 text-card-foreground">
        {milestone.title}
      </h3>
      <p className="font-default text-sm leading-relaxed text-muted-foreground mb-4">
        {milestone.description}
      </p>
      {milestone.image && <div className={`overflow-hidden rounded-md ${align === "right" ? "md:ml-auto" : ""}`} style={{
      maxWidth: "240px"
    }}>
          <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.title} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
        </div>}
    </div>;
}
export default function Milestones() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: presentRef,
    isVisible: presentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="64" id="professional-milestones" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-10 max-w-6xl">
        <div ref={headerRef} className={`max-w-2xl mb-20 md:mb-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="font-mono text-xs tracking-[0.25em] uppercase mb-6" style={{
          color: "var(--chart-2)"
        }}>
            Our Journey · Growth
          </div>
          <h2 className="font-serif font-default text-5xl md:text-6xl leading-[1.05] text-foreground mb-6" style={{
          fontWeight: 400
        }}>
            A decade of
            <br />
            <em className="italic" style={{
            fontWeight: 300
          }}>
              revenue mastery.
            </em>
          </h2>
          <p className="font-default text-base md:text-lg leading-relaxed" style={{
          color: "var(--muted-foreground)"
        }}>
            From our early days in revenue consulting to managing complex tech stacks for global properties, we have built our reputation on data-driven results.
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.6
        }} aria-hidden="true" />
          <div className="md:hidden absolute left-2 top-0 bottom-0 w-px" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.4
        }} aria-hidden="true" />

          {milestones.map((milestone, i) => <MilestoneRow key={milestone.year} milestone={milestone} index={i} />)}

          <div ref={presentRef} data-index={milestones.length} className={`relative flex flex-col items-center text-center pt-8 transition-all duration-700 ease-out ${presentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 z-10">
              <div className="relative flex items-center justify-center">
                <div className="size-5 rounded-lg" style={{
                backgroundColor: "var(--chart-2)"
              }} />
                <div className="absolute size-10 rounded-lg opacity-30 animate-pulse" style={{
                backgroundColor: "var(--chart-2)"
              }} />
              </div>
            </div>
            <div className="mt-12 max-w-xl mx-auto">
              <div className="font-mono text-xs tracking-[0.25em] uppercase mb-4" style={{
              color: "var(--chart-2)"
            }}>
                Current Focus
              </div>
              <div className="font-serif text-6xl md:text-7xl leading-none font-light mb-6" style={{
              color: "var(--primary)",
              fontVariantNumeric: "tabular-nums"
            }}>
                2025
              </div>
              <h3 className="font-serif font-default text-2xl md:text-3xl leading-snug text-foreground mb-4">
                Ready to scale your revenue?
              </h3>
              <p className="font-default text-base leading-relaxed mb-8" style={{
              color: "var(--muted-foreground)"
            }}>
                Partner with us to optimize your hotel's performance and unlock new revenue streams through expert consulting.
              </p>
              <Button asChild size="lg" variant="default" className="rounded-lg font-default">
                <Link to="/ContactPage">
                  Book a consultation
                  <ArrowRight className="ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}